// Regional pricing calc — frontend mirror of server/src/lib/pricing.ts. Keep the
// two identical; both are unit-tested against the guide's worked examples.

export type PricingRegion = 'NORTHEAST' | 'SOUTHERN' | 'OTHER';

export interface PricingInput {
  region: PricingRegion;
  tons: number;
  deliveredPriceCents?: number | null;
  stonePriceCents?: number | null;
  deliveryRateCents?: number | null;
  isPickup?: boolean;
  tierDiscountCents?: number | null;
  applyTierDiscount?: boolean;
  /** Flat per-load direct-ship fee (OrderItem.carrierRate) — its own line, no
   * per-ton scaling and no fuel surcharge. */
  directShipFeeCents?: number | null;
  materialPct: number;
  deliveryPct: number;
}

export interface PricingLine {
  key: 'delivered' | 'stone' | 'delivery' | 'directShip';
  label: string;
  /** Flat lines (direct ship) are a single amount, not tons × rate + surcharge. */
  flat?: boolean;
  basePerTonCents: number;
  discountPerTonCents: number;
  perTonCents: number;
  tons: number;
  subtotalCents: number;
  surchargePct: number;
  surchargeCents: number;
  lineTotalCents: number;
}

export interface PricingBreakdown {
  region: PricingRegion;
  priced: boolean;
  lines: PricingLine[];
  grandTotalCents: number;
  productPriceCentsSnapshot: number;
  tierDiscountCentsSnapshot: number;
  materialPct: number;
  deliveryPct: number;
}

const centsMul = (perTonCents: number, tons: number) => Math.round(perTonCents * tons);
const pctOf = (cents: number, pct: number) => Math.round((cents * pct) / 100);

function unpriced(input: PricingInput): PricingBreakdown {
  return {
    region: input.region,
    priced: false,
    lines: [],
    grandTotalCents: 0,
    productPriceCentsSnapshot: 0,
    tierDiscountCentsSnapshot: 0,
    materialPct: input.materialPct,
    deliveryPct: input.deliveryPct,
  };
}

// Append the flat direct-ship fee (if any) and total everything up.
function finalize(
  region: PricingRegion,
  lines: PricingLine[],
  productPriceCentsSnapshot: number,
  tierDiscountCentsSnapshot: number,
  input: PricingInput
): PricingBreakdown {
  const directShip = input.directShipFeeCents ?? 0;
  const allLines = [...lines];
  if (directShip > 0) {
    allLines.push({
      key: 'directShip',
      label: 'Direct ship fee',
      flat: true,
      basePerTonCents: 0,
      discountPerTonCents: 0,
      perTonCents: 0,
      tons: 0,
      subtotalCents: directShip,
      surchargePct: 0,
      surchargeCents: 0,
      lineTotalCents: directShip,
    });
  }
  return {
    region,
    priced: true,
    lines: allLines,
    grandTotalCents: allLines.reduce((sum, l) => sum + l.lineTotalCents, 0),
    productPriceCentsSnapshot,
    tierDiscountCentsSnapshot,
    materialPct: input.materialPct,
    deliveryPct: input.deliveryPct,
  };
}

export function computeOrderPricing(input: PricingInput): PricingBreakdown {
  const tons = Number(input.tons);
  if (!Number.isFinite(tons) || tons <= 0) return unpriced(input);

  if (input.region === 'NORTHEAST') {
    const perTon = input.deliveredPriceCents ?? null;
    if (perTon == null) return unpriced(input);
    const subtotal = centsMul(perTon, tons);
    const surcharge = pctOf(subtotal, input.deliveryPct);
    const line: PricingLine = {
      key: 'delivered',
      label: 'Delivered price',
      basePerTonCents: perTon,
      discountPerTonCents: 0,
      perTonCents: perTon,
      tons,
      subtotalCents: subtotal,
      surchargePct: input.deliveryPct,
      surchargeCents: surcharge,
      lineTotalCents: subtotal + surcharge,
    };
    return finalize('NORTHEAST', [line], perTon, 0, input);
  }

  if (input.region === 'SOUTHERN') {
    const base = input.stonePriceCents ?? null;
    if (base == null) return unpriced(input);

    const discountPerTon =
      input.applyTierDiscount && input.tierDiscountCents ? input.tierDiscountCents : 0;
    const effectiveStone = Math.max(0, base - discountPerTon);
    const stoneSubtotal = centsMul(effectiveStone, tons);
    const stoneSurcharge = pctOf(stoneSubtotal, input.materialPct);
    const stoneLine: PricingLine = {
      key: 'stone',
      label: 'Stone (picked up)',
      basePerTonCents: base,
      discountPerTonCents: discountPerTon,
      perTonCents: effectiveStone,
      tons,
      subtotalCents: stoneSubtotal,
      surchargePct: input.materialPct,
      surchargeCents: stoneSurcharge,
      lineTotalCents: stoneSubtotal + stoneSurcharge,
    };

    const lines: PricingLine[] = [stoneLine];

    const deliveryRate = input.deliveryRateCents ?? 0;
    if (!input.isPickup && deliveryRate > 0) {
      const deliverySubtotal = centsMul(deliveryRate, tons);
      const deliverySurcharge = pctOf(deliverySubtotal, input.deliveryPct);
      lines.push({
        key: 'delivery',
        label: 'Delivery',
        basePerTonCents: deliveryRate,
        discountPerTonCents: 0,
        perTonCents: deliveryRate,
        tons,
        subtotalCents: deliverySubtotal,
        surchargePct: input.deliveryPct,
        surchargeCents: deliverySurcharge,
        lineTotalCents: deliverySubtotal + deliverySurcharge,
      });
    }

    return finalize('SOUTHERN', lines, effectiveStone, discountPerTon, input);
  }

  return unpriced(input);
}
